import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { resolveImg } from "@/lib/api";
import { Phone, MapPin, Clock, Truck } from "lucide-react";

export const Footer = ({ settings }) => {
  const { t, pick } = useLang();
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings?.address || "")}`;

  return (
    <footer id="visit" className="bg-darkink text-dough pt-24 md:pt-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="overline text-maize mb-4">{t("visit_kicker")}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight max-w-2xl">{t("visit_title")}</h2>
          <p className="font-body text-dough/60 mt-5 max-w-lg">{t("order_intro")}</p>
        </Reveal>

        {/* order buttons */}
        <div className="flex flex-wrap gap-4 mt-10" data-testid="order-buttons">
          <a
            href={settings?.doordash_url || "#"}
            target="_blank" rel="noopener noreferrer"
            data-testid="order-doordash"
            className="inline-flex items-center gap-2 font-label font-bold bg-terracotta text-dough rounded-full px-7 py-4 hover:bg-maize hover:text-ink transition-colors duration-300"
          >
            <Truck size={18} /> DoorDash
          </a>
          <a
            href={settings?.ubereats_url || "#"}
            target="_blank" rel="noopener noreferrer"
            data-testid="order-ubereats"
            className="inline-flex items-center gap-2 font-label font-bold bg-quetzal text-dough rounded-full px-7 py-4 hover:bg-maize hover:text-ink transition-colors duration-300"
          >
            <Truck size={18} /> Uber Eats
          </a>
          <a
            href={`tel:${(settings?.phone_1 || "").replace(/[^0-9]/g, "")}`}
            data-testid="order-call"
            className="inline-flex items-center gap-2 font-label font-bold border border-dough/25 rounded-full px-7 py-4 hover:bg-dough hover:text-ink transition-colors duration-300"
          >
            <Phone size={18} /> {t("call")}
          </a>
        </div>

        {/* info grid */}
        <div className="grid md:grid-cols-3 gap-8 mt-16 border-t border-dough/15 pt-14">
          <Reveal>
            <div className="flex flex-col gap-3">
              <Clock className="text-maize" size={22} />
              <h3 className="overline text-dough/50">{t("hours")}</h3>
              <p className="font-body text-dough/90">{pick(settings, "hours")}</p>
              <p className="font-body text-dough/60 text-sm">{pick(settings, "delivery_note")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col gap-3">
              <MapPin className="text-maize" size={22} />
              <h3 className="overline text-dough/50">{t("address")}</h3>
              <p className="font-body text-dough/90">{settings?.address}</p>
              <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="font-label text-terracotta font-bold text-sm link-underline w-fit">
                {t("directions")} →
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-col gap-3">
              <Phone className="text-maize" size={22} />
              <h3 className="overline text-dough/50">{t("phone")}</h3>
              <a href={`tel:${(settings?.phone_1 || "").replace(/[^0-9]/g, "")}`} className="font-body text-dough/90 link-underline w-fit">{settings?.phone_1}</a>
              {settings?.phone_2 && (
                <a href={`tel:${settings.phone_2.replace(/[^0-9]/g, "")}`} className="font-body text-dough/90 link-underline w-fit">{settings.phone_2}</a>
              )}
            </div>
          </Reveal>
        </div>

        {/* bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-t border-dough/15 mt-16 py-10">
          <div className="flex items-center gap-3">
            <img src={resolveImg(settings?.logo_url)} alt="logo" className="h-12 w-12 rounded-full object-cover ring-2 ring-dough/20" />
            <div>
              <p className="font-serif text-lg">La Panadería Totopan</p>
              <p className="font-body text-dough/50 text-sm">{pick(settings, "tagline")}</p>
            </div>
          </div>
          <p className="font-body text-dough/40 text-sm">
            © {new Date().getFullYear()} La Panadería Totopan. {t("rights")}
          </p>
        </div>
      </div>
    </footer>
  );
};
